import { AnimatePresence, motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'

import { Typography } from '@cockpitai/components/atoms/Typography'
import { usePageTracking } from '@cockpitai/hooks/usePageTracking'
import { PropsWithChildren } from 'react'
import { AnimationPageLogin, Icons, UnderlineMotion } from '../../atoms'

const defaultAnimation = {
  initial: { opacity: 0, y: -50 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -50 },
}

type TState = {
  title: string
  descriptions: string[]
}

export function AuthTemplate({ children }: PropsWithChildren) {
  usePageTracking()

  const route = useLocation()

  const { title, descriptions }: TState = route.state ?? {
    title: import.meta.env.VITE_CONSULTANT === 'true' ? 'Bem-vindo ao ConsultaAI' : 'Bem-vindo ao ChamaAI',
    descriptions: import.meta.env.VITE_CONSULTANT === 'true' ? ['Entre com seus dados.'] : ['Entre com seus dados do Portal Turbo.'],
  }

  const isInitialPage = route.pathname === '/'

  const isAuthenticatedPage = !route.pathname.includes('autenticacao')

  return (
    <div className="h-screen bg-gray-900 p-10">
      {isAuthenticatedPage ? (
        <div className="mx-auto h-full w-full max-w-screen-1xl grid-cols-12 gap-16 lg:grid">
          <div className="flex h-full flex-col justify-center lg:col-span-5 xl:col-span-4">
            <div className="relative mb-4 flex w-36 items-center space-x-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 1,
                  delay: 0.5,
                  type: 'tween',
                  stiffness: 30,
                }}>
                {import.meta.env.VITE_CONSULTANT === 'true' ? (
                  <img
                    src="/consultant-logo-auth.svg"
                    alt="ConsultaAI"
                    className="aspect-auto object-contain w-[144px]"
                  />
                ) : (
                  <img
                    src="/logo-auth.svg"
                    alt="ChamaAI"
                    className="aspect-auto object-contain"
                  />
                )}
              </motion.div>
            </div>

            <AnimatePresence initial={false}>
              {!isInitialPage && (
                <motion.div
                  {...defaultAnimation}
                  transition={{ delay: 0.2, duration: 0.5 }}>
                  <UnderlineMotion className="bg-white bg-opacity-85">
                    <Link
                      to="."
                      className="flex items-center text-white text-opacity-85">
                      <Icons.ArrowLeft className="mr-2 text-white text-opacity-85" />
                      Voltar
                    </Link>
                  </UnderlineMotion>
                </motion.div>
              )}
            </AnimatePresence>

            <>
              <motion.h2
                {...defaultAnimation}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mt-10 font-outfit text-3xl font-semibold text-white">
                {title}
              </motion.h2>

              <motion.div
                {...defaultAnimation}
                transition={{ delay: 0.5, duration: 0.5 }}>
                {descriptions?.map((item, index) => (
                  <Typography
                    key={index}
                    type="subtitleTwo"
                    className="text-gray-300">
                    {item}
                  </Typography>
                ))}
              </motion.div>
            </>

            {children}
          </div>

          <div className="bg-login-page shadow-xs hidden flex-col justify-center space-y-4 rounded-3xl px-16 shadow-gray-500 lg:col-span-7 lg:flex xl:col-span-8">
            <motion.div
              {...defaultAnimation}
              transition={{ delay: 0.7, duration: 0.5 }}>

              {import.meta.env.VITE_CONSULTANT === 'true' ?
                <img
                  src="/Consultant_ChamaAI_logo.svg"
                  alt="Consultor ChamaAI"
                  className="aspect-auto object-contain w-[280px]"
                /> : (
                  <img
                    src="/ChamaAI_logo.svg"
                    alt="ChamaAI"
                    className="aspect-auto object-contain"
                  />
                )
              }

            </motion.div>

            <motion.p
              {...defaultAnimation}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="max-w-xl font-outfit text-3xl text-white text-opacity-80">
              Sua copilota de IA comercial para navegar com precisão e inteligência no mercado.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.5 }}>
              <AnimationPageLogin />
            </motion.div>
          </div>
        </div>
      ) : (
        <div className="mx-auto flex h-full max-w-[300px] flex-wrap items-center justify-center">
          <div className="flex w-full flex-col items-center justify-center">
            <div className="relative mb-4 flex items-center justify-center space-x-2">
              <div>
                {import.meta.env.VITE_CONSULTANT === 'true' ? <Icons.ConsultaAI width={40} height={40} /> : <Icons.SantanderCircle width={40} height={40} />}
              </div>
              <h1 className="font-outfit text-lg font-semibold text-white">
                {import.meta.env.VITE_CONSULTANT === 'true' ? 'ConsultaAI' : 'ChamaAI'}
              </h1>
            </div>
            <div className="-mt-10 w-full">{children}</div>
          </div>
        </div>
      )}
    </div>
  )
}
